import dayjs from 'dayjs';
import Timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { v4 as uuidv4 } from 'uuid';
import { convertDOBToAgeRange } from '@/utilities/dobHelper';
import { serviceDescriptionMapping } from '@/types/seeker';
import { Child, ChildDOB } from '@/types/seekerCC';
import { Intent, PartsOfDay } from '@/types/common';

dayjs.extend(Timezone);
dayjs.extend(utc);

export function getDateFromIntent(intent: Intent | undefined, todayLocalTZ: dayjs.Dayjs) {
  const todayEST = todayLocalTZ.tz('America/New_York');
  const processedIntent = intent ?? 'UNDECIDED';
  switch (processedIntent) {
    case 'WEEK':
      return todayLocalTZ.add(7, 'day').format('YYYY-MM-DD');
    case 'MONTH':
      return todayLocalTZ.add(1, 'month').format('YYYY-MM-DD');
    case 'NOW':
    case 'UNDECIDED':
    default:
      // If it is 9pm or later EST we want to push to the next day
      return todayEST.add(3, 'hour').format('YYYY-MM-DD');
  }
}

export function constructBlocksFromCareTimes(careTimes: PartsOfDay) {
  const blocks = [];

  if (careTimes.morning && careTimes.afternoon && careTimes.evening && careTimes.overnight) {
    blocks.push({ start: '00:00', end: '24:00' });
  } else {
    // flip order to get desired result
    if (careTimes.overnight) {
      blocks.push({ start: '00:00', end: '06:00' });
    }
    if (careTimes.morning) {
      blocks.push({ start: '06:00', end: '12:00' });
    }
    if (careTimes.afternoon) {
      blocks.push({ start: '12:00', end: '18:00' });
    }
    if (careTimes.evening) {
      blocks.push({ start: '18:00', end: '24:00' });
    }
  }

  return { blocks };
}

function replaceSmartPunctuation(text: string) {
  return (
    text
      // replace left & right single quotes with '
      .replace(/[\u2018\u2019]/g, "'")
      // replace left & right double quotes with "
      .replace(/[\u201C\u201D]/g, '"')
      // replace en dashes with "-"
      .replace(/\u2013/g, '-')
      // replace em dashes with "--"
      .replace(/\u2014/g, '--')
      // replace … with "..."
      .replace(/\u2026/g, '...')
  );
}

export function generateDescription(
  lovedOneDetails: string | undefined,
  caregiverDetails: string | undefined,
  servicesNeeded: string[] | undefined,
  caregiverGender: string | null
) {
  let freeFormTextSupplied = false;

  let description = '';
  if (lovedOneDetails && lovedOneDetails.length > 15) {
    freeFormTextSupplied = true;
    description += `About who needs care: ${replaceSmartPunctuation(lovedOneDetails)}\n\n`;
  }

  if (caregiverDetails && caregiverDetails.length > 15) {
    freeFormTextSupplied = true;
    description += `About the care needs: ${replaceSmartPunctuation(caregiverDetails)}\n\n`;
  }

  if (servicesNeeded && servicesNeeded.length > 0) {
    const descriptions = servicesNeeded
      .map((service) => serviceDescriptionMapping[service])
      .filter(Boolean);
    const serviceDescription = [
      descriptions.slice(0, -1).join(', '),
      descriptions.slice(-1)[0],
    ].join(descriptions.length < 2 ? '' : ', and ');
    description += `Services needed include: ${serviceDescription}.\n\n`;
  }

  if (caregiverGender) {
    const gender = caregiverGender === 'FEMALE' ? 'female' : 'male';
    description += `Preference for a caregiver who is ${gender}.\n`;
  }
  return [description, freeFormTextSupplied];
}

export function constructChildren(careChildren: Child[], careChildrenDOB: ChildDOB[]) {
  const children: Child[] = [];
  careChildren.forEach((c: Child) => {
    children.push({
      gender: c.gender,
      ageRange: c.ageRange,
    });
  });
  for (let i = 0; i < careChildrenDOB.length; i += 1) {
    const dob = careChildrenDOB[i];

    const child = {
      id: uuidv4(),
      ageRange: convertDOBToAgeRange(dob ?? null),
      approximateDoB: dob,
    };

    if (i < children.length) {
      children[i] = {
        ...children[i],
        ...child,
      };
    } else {
      children.push({
        ...children[i],
        ...child,
      });
    }
  }

  return children;
}
